import Button from 'components/Button';
import Error from 'components/form/Error';
import { FlexColumn } from 'components/form/generic';
import Info from 'components/form/Info';
import Textarea from 'components/form/Inputs/Textarea';
import Success from 'components/form/Success';
import InterviewInfo from 'components/Interview/Info';
import Field from 'components/profileNavigation/Field';
import Space from 'components/Space';
import { FC, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { mutate } from 'swr';
import privateAPI from 'utils/privateAxiosInstance';
import { ReviewPageContext } from '.';

const Container = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  & > div {
    width: 100%;
  }
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  row-gap: 1rem;
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  max-width: 60rem;
  button {
    margin-left: 0.5rem;
  }
`;

type State = {
  needs: string;
  notes: string;

  progressComment: string;

  qualityComment: string;
};

type Errors = Record<'general', boolean>;

const initialErrors: Errors = {
  general: false,
};

const ReviewEdit: FC = () => {
  const { interview } = useContext(ReviewPageContext);

  const [state, setState] = useState<State>({
    needs: '',
    notes: '',
    progressComment: '',
    qualityComment: '',
  });
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (interview?.menteeReview) {
      setState({
        needs: interview.menteeReview.needs || '',
        notes: interview.menteeReview.notes || '',
        progressComment: interview.menteeReview.progressComment || '',
        qualityComment: interview.menteeReview.qualityComment || '',
      });
    }
  }, [interview]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> &
    React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setErrors(initialErrors);
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setErrors(initialErrors);
    setSuccess(false);

    setLoading(true);

    privateAPI
      .post(`/mentee-interview-review/${interview?.id}`, {
        ...state,
      })
      .then(() => {
        mutate(`/interview/details/mentee/${interview?.id}`);
        mutate('/interview/mentee');
        setSuccess(true);
      })
      .catch(() => {
        setErrors({ ...initialErrors, general: true });
      })
      .finally(() => {
        setLoading(false);
        window.scrollTo({ top: 0 });
      });
  };

  return (
    <Container onSubmit={handleSubmit}>
      {errors.general && (
        <>
          <Error>Une erreur est survenue.</Error>
          <Space height={1} />
        </>
      )}
      {success && (
        <>
          <Success>Votre compte-rendu a été enregistré avec succès.</Success>
          <Space height={1} />
        </>
      )}
      <Info style={{ marginBottom: '1rem' }}>
        Cette fiche n'est pas visible par votre mentor. Elle vous sert
        simplement à garder une trace de votre progression au cours de votre
        mentorat. L'administrateur de l'Institut peut avoir accès à ces
        informations.
      </Info>
    <DescriptionContainer>
      <InterviewInfo interview={interview} />
    </DescriptionContainer>

      <Field label="Progression">
        <FlexColumn gap={1}>
          <Textarea
            placeholder="Suivi de la progression"
            value={state.progressComment}
            name="progressComment"
            onChange={handleChange}
            rows={8}
            characterLimit={2000}
            noEnterFireSubmit
          />
        </FlexColumn>
      </Field>
      <Field label="Mes échanges">
        <FlexColumn gap={1}>
          <Textarea
            placeholder="Ce que je retiens de nos échanges"
            value={state.qualityComment}
            name="qualityComment"
            onChange={handleChange}
            rows={8}
            characterLimit={2000}
            noEnterFireSubmit
          />
        </FlexColumn>
      </Field>

      <Field label="Objectifs et axes de progression pour la prochaine fois">
        <Textarea
          placeholder="Besoins exprimés"
          value={state.needs}
          name="needs"
          onChange={handleChange}
          rows={8}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </Field>
      <Field label="Prise de note">
        <Textarea
          placeholder="Notes"
          value={state.notes}
          name="notes"
          onChange={handleChange}
          rows={8}
          characterLimit={2000}
          noEnterFireSubmit
        />
      </Field>

      <ButtonContainer>
        <Button type="submit" color="primary" loading={loading}>
          Enregistrer
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default ReviewEdit;
